import "./tableView.scss";

import { clone, isNil } from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { Fragment, PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";

import { asDropTarget } from "../../components/dragAndDrop/DroppableComponent";
import { OUTLINE_CELL_ROW_GUTTER_WIDTH, TABLE_DRAG_DROP_BORDER_SIZE } from "../../constants";
import { DragDropTypes } from "../../constants/dragDropTypes";
import { TABLE_VIEW } from "../../constants/identifiers";
import interfaceMessages from "../../constants/interfaceMessages";
import PubSubTopics from "../../constants/pubSubTopics";
import { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import tableDropAction from "../../constants/tableDropAction";
import tableRowTypes from "../../constants/tableRowTypes";
import { immutableRecords, PropTypeShapes } from "../../types";
import {
  isDragMeasuringTimespan,
  tableDropPositionWithNesting,
  tableDropRowIndex,
  tableDropRowPreviousStartDate
} from "../../utils/dropFunctions";
import { withContainerContext } from "../container/ContainerContext";
import { withScrollPositionContext } from "../scrollable/ScrollPositionContext";
import withScrollableView from "../scrollable/withScrollableView";
import TableRowView, { DraggableTableRowView } from "./TableRowView";
import utils from "./utils";

// import debugUtils from "../../utils/debugUtils";

let sortByTooltipTimer;

const canDropOnComponent = (props, dragItem, positions) => {
  if (isDragMeasuringTimespan(dragItem)) {
    return false;
  }
  if (dragItem.itemType === DragDropTypes.TABLE_COLUMN) {
    return false;
  }

  const dropRows = props.data.rows;
  const mouseY = positions.targetPointerPos.y;
  const result = tableDropPositionWithNesting(
    dropRows,
    mouseY,
    dragItem,
    props.canItemDropOnRow,
    TABLE_DRAG_DROP_BORDER_SIZE,
    props.disableReordering,
    props.displayItems
  );

  const newDropRowIndex =
    result.dropAction != tableDropAction.DropOnRow ? tableDropRowIndex(dropRows, mouseY, dragItem) : null;
  const dropDate =
    result.dropAction != tableDropAction.DropOnRow
      ? tableDropRowPreviousStartDate(dropRows, newDropRowIndex, props.displayItems)
      : null;

  const canDropItems = props.requestCanDropItems(
    dragItem.multipleItemIds,
    dropRows,
    dropDate,
    result.dropAction,
    result.dropRow,
    newDropRowIndex,
    props.adjustItemEndWhenChangingStart
  );
  const canDrop = canDropItems.canDrop;

  return canDrop && props.allowRowDrag && props.canRowDrop;
};

const dropCallbacks = {
  onEnter: (props, dragItem, dropTarget) => {
    dropTarget.component.onDragEnter();
    if (props.onDragEnter) {
      props.onDragEnter(dragItem.multipleItemIds);
    }
  },

  onLeave: (props, _dragItem, dropTarget) => {
    dropTarget.component.onDragLeave();
    if (props.onDragLeave) {
      props.onDragLeave();
    }
  },

  getDragMultipleItemIds(props, dragItem, _dropTarget) {
    // Note: this function will be fired before the click event and
    // the state changing. i.e. the selected row ids will be the result
    // of the last selection but not the recent one.
    // Please see the details in the comments in ATR-20
    return props.selectedRowIds.contains(dragItem.itemId)
      ? props.sortedItemIds.filter(id => props.selectedRowIds.contains(id)).toArray()
      : [dragItem.itemId];
  },

  getDragStartParams(props, dragItem, positions, dropTarget) {
    return dropTarget.component.getDragStartParams(props, dragItem, positions, dropTarget);
  },

  onHover: (props, dragItem, positions, dropTarget) => {
    return dropTarget.component.onHover(props, dragItem, positions, dropTarget);
  },

  onDrop: (props, dragItem, positions, dropTarget) => {
    dropTarget.component.onDragDrop();
    const canDrop = canDropOnComponent(props, dragItem, positions);
    if (canDrop) {
      dropTarget.component.onDrop(props, dragItem, positions, dropTarget);
    } else {
      dropTarget.component.cancelDrop();
    }
  },

  getDropPreviewForItem(inProps, dragItem, _dropTarget) {
    if (dragItem.itemType === DragDropTypes.TABLE_COLUMN) {
      return;
    }
    const obj = inProps.requestDragPreviewDataForItem(dragItem.itemId);
    if (!obj) {
      return;
    }
    const visibleColumns = inProps.data.columns;
    const visibleCells = obj.cells;
    return (
      <TableRowView
        key={obj.id}
        {...obj.toJS()}
        index={0}
        top={0}
        left={0}
        tableId={inProps.tableId}
        viewId={inProps.viewId}
        isSelected={inProps.selectedRowIds && inProps.selectedRowIds.contains(obj.id)}
        cellSelection={inProps.cellSelection}
        focusedCell={inProps.focusedCell}
        tableIsActive={inProps.tableIsActive}
        tableShouldReacquireFocus={inProps.tableShouldReacquireFocus}
        cells={visibleCells}
        allCells={obj.cells}
        columns={visibleColumns}
        allColumns={inProps.data.columns}
        calendar={inProps.calendar}
        autoAdjustEndDate={inProps.autoAdjustEndDate}
        onToggleChildren={null}
        onGutterClick={null}
        onCellClick={() => {}}
        onCellCut={() => {}}
        onCellCopy={() => {}}
        onCellPaste={() => {}}
        onCellDelete={() => {}}
        onCellDoubleClick={() => {}}
        onValueChanged={() => {}}
        onMultiValueChanged={() => {}}
        moveFocus={() => {}}
        getCellId={() => "preview"}
        canDrag={false}
        showGutter={true}
        gutterIcon={inProps.rowGutterIcon}
        gutterIconColor={inProps.rowGutterIconColor}
        isDraggingRow={false}
        isDragPreview={true}
        getCustomCellOptions={null}
        fromColumn={0}
        toColumn={inProps.data.columns.size}
        registerCellFocusCallback={() => {}}
        unregisterCellFocusCallback={() => {}}
        onCellPopoverToggle={() => {}}
      />
    );
  }
};

const getVisibleRows = nextProps => {
  const rows = nextProps.data.rows;
  const scrollTop = nextProps.yScrollPosition.viewport.min;
  const scrollBottom = nextProps.yScrollPosition.viewport.max;

  return rows.filter(r => {
    const bottom = r.top + r.height;
    return bottom > scrollTop && r.top < scrollBottom;
  });
};

class TableBodyView extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      visibleRows: getVisibleRows(props),
      sortByTooltipOpen: false
    };
    this.onScrollToCellReceived = this.onScrollToCellReceived.bind(this);

    this.onStickyNodeScroll = this.onStickyNodeScroll.bind(this);

    this._liveScrollListeners = {
      onLiveScrollStart: this.onLiveScrollStart.bind(this),
      onLiveScrollUpdate: this.onLiveScrollUpdate.bind(this),
      onLiveScrollStop: this.onLiveScrollStop.bind(this)
    };

    this._horizontalScrollerActive = false;
    this._verticalScrollerActive = false;
    this._liveLeft = 0;
    this._liveTop = 0;

    this._stickyDomNode = null;
    this._stickyTransformNode = null;

    this.getStyles = this.getStyles.bind(this);
    this.applyStyles = this.applyStyles.bind(this);
    this.getDragStartParams = this.getDragStartParams.bind(this);
    this.renderSelectionOutlines = this.renderSelectionOutlines.bind(this);

    // SortBy tooltip

    this._sortByColNode = null;
    this._sortByColTooltipNode = null;
    this._sortByColLeft = 0;
    this._sortByColWidth = 0;
    this._closeTooltipOnClick = false;

    this.getTooltipStyle = this.getTooltipStyle.bind(this);
    this.applyTooltipStyle = this.applyTooltipStyle.bind(this);
    this.renderTooltip = this.renderTooltip.bind(this);
    this.showSortByTooltipIfRequired = this.showSortByTooltipIfRequired.bind(this);
    this.closeSortByTooltip = this.closeSortByTooltip.bind(this);
    this.resetSortByTooltipTimer = this.resetSortByTooltipTimer.bind(this);
  }

  componentDidMount() {
    this.applyStyles();
    this._pubSubScrollToItemToken = PubSub.subscribe(PubSubTopics.scrollToCell, this.onScrollToCellReceived);
    this.props.registerDropTarget(this, dropCallbacks);
    this.props.xScrollActions.registerForLiveUpdates(this._liveScrollListeners);
    this.props.yScrollActions.registerForLiveUpdates(this._liveScrollListeners);
  }

  componentWillReceiveProps(nextProps) {
    // debugUtils.logPropChanges("TableBodyView", this.props, nextProps, true);
    const visibleRows = getVisibleRows(nextProps);
    this.setState({ visibleRows });
  }

  componentDidUpdate() {
    this.applyStyles();
  }

  componentWillUnmount() {
    PubSub.unsubscribe(this._pubSubScrollToItemToken);
    this.props.xScrollActions.unregisterForLiveUpdates(this._liveScrollListeners);
    this.props.yScrollActions.unregisterForLiveUpdates(this._liveScrollListeners);
    clearTimeout(sortByTooltipTimer);
  }

  onHover(props, dragItem, positions, _dropTarget) {
    if (isDragMeasuringTimespan(dragItem)) {
      return {
        message: interfaceMessages.DRAG_DROP_MEASURE_ACROSS_VIEWS_NOT_ALLOWED,
        messageType: "error"
      };
    }
    if (dragItem.itemType === DragDropTypes.TABLE_COLUMN) {
      return {
        message: "Must be dragged in table header",
        messageType: "error"
      };
    }

    // TODO: Data in props.data.rows is structurally different than in relationship view
    const mouseY = positions.targetPointerPos.y;
    const dropRows = this.props.data.rows;
    const result = tableDropPositionWithNesting(
      dropRows,
      mouseY,
      dragItem,
      this.props.canItemDropOnRow,
      TABLE_DRAG_DROP_BORDER_SIZE,
      props.disableReordering,
      props.displayItems
    );

    const newDropRowIndex = tableDropRowIndex(dropRows, mouseY, dragItem);

    if (result.dropAction != tableDropAction.DropOnRow) {
      if (this._dropRowIndex != newDropRowIndex) {
        this._dropRowIndex = newDropRowIndex;
        this._dropDate = tableDropRowPreviousStartDate(dropRows, newDropRowIndex, this.props.displayItems);
      }
    } else {
      this._dropRowIndex = null;
      this._dropDate = null;
    }

    const canDropItems = this.props.requestCanDropItems(
      dragItem.multipleItemIds,
      dropRows,
      this._dropDate,
      result.dropAction,
      result.dropRow,
      newDropRowIndex,
      this.props.adjustItemEndWhenChangingStart
    );

    const canDrop = canDropItems.canDrop;

    if (!canDrop) {
      this._dropRow = null;
      this._dropAction = null;
      this._dropAsChild = null;
      this.props.onLiveRowDrag
        ? this.props.onLiveRowDrag(dragItem.multipleItemIds, this._dropRow, this._dropAction)
        : null;
      return {
        message: canDropItems.message,
        messageType: "error"
      };
    }

    this._dropRow = result.dropRow;
    this._dropAction = result.dropAction;
    this._dropAsChild = result.dropAsChild;

    // And notify whatever is listening to the table that this shit is going down...
    this.props.onLiveRowDrag
      ? this.props.onLiveRowDrag(dragItem.multipleItemIds, this._dropRow, this._dropAction)
      : null;

    if (canDrop) {
      return {
        message: canDropItems.message,
        messageType: "success"
      };
    }
  }

  onDrop(props, dragItem, _positions, _dropTarget) {
    const fromExternalSource = props.viewId !== dragItem.sourceView;
    // Now move the dragged item to the correct index...
    this.props.onRowDrop && this._dropRow && this._dropAction
      ? this.props.onRowDrop(dragItem.multipleItemIds, this._dropRow, this._dropAction, fromExternalSource)
      : null;
    this._dropRow = null;
    this._dropAction = null;
  }

  cancelDrop() {
    this._dropRow = null;
    this._dropAction = null;
    this.props.onLiveRowDrag ? this.props.onLiveRowDrag(null, this._dropRow, this._dropAction) : null;
  }

  getDragStartParams(_props, _dragItem, positions, _dropTarget) {
    const screenPointerPos = clone(positions.screenPointerPos);
    const sourceRectPointerPosX = screenPointerPos.x - positions.sourceRect.left;
    const sourceRectPointerPosY = screenPointerPos.y - positions.sourceRect.top;
    return {
      sourceRectPointerPosX,
      sourceRectPointerPosY
    };
  }

  onDragEnter() {
    this.showSortByTooltipIfRequired();
  }

  onDragLeave() {
    this.closeSortByTooltip();
  }

  onDragDrop() {
    this.closeSortByTooltip();
  }

  getVisibleRows(rows) {
    const scrollTop = this.props.yScrollPosition.viewport.min;
    const scrollBottom = this.props.yScrollPosition.viewport.max;

    const visible = rows.filter(r => {
      const bottom = r.top + r.height;
      return bottom > scrollTop && r.top < scrollBottom;
    });
    return visible;
  }

  scrollToFoldedRow(scrollData) {
    if (this.props.getItemIdsToExpand) {
      const itemIdsToExpand = this.props.getItemIdsToExpand(scrollData.row);
      // In the getItemIdsToExpand, the super(s) have been expanded if they can.
      // Should check whether the expanded ids are existed in the rows now.
      // In case the filter, item type etc make that target item not be able to
      // appear in this table.
      // See https://aeontimeline.atlassian.net/browse/ATR-1670
      // It used to lead to infinite loop when adding item with filter.
      // Added the flag to stop the repeating scrolling when it has tried expanding
      // the folded rows.
      // See https://aeontimeline.atlassian.net/browse/ATR-1899
      if (itemIdsToExpand.length > 0 && itemIdsToExpand.every(id => this.props.data.rows.find(r => r.id === id))) {
        this.onScrollToCellReceived(null, scrollData, true);
      }
    }
  }

  onScrollToCellReceived(msg, data, doNotScrollAgain = false) {
    const { tableId, row, column, animate } = data;

    const r = this.props.data.rows.find(r => r.id === row);
    const c = this.props.data.columns.find(c => c.id === column);

    if (tableId !== this.props.tableId || !c || c.index < 0 || c.index > this.props.data.columns.size) {
      return;
    } else if (!r) {
      if (!doNotScrollAgain) {
        this.scrollToFoldedRow(data);
      }
    } else {
      const firstCol = this.props.data.columns.get(0);
      const firstLeft = firstCol.left;

      const left = Math.max(0, c.left - firstLeft);

      const rect = immutableRecords.FlatRectRecord({
        left,
        right: left + c.width,
        width: c.width,
        top: r.top,
        height: r.height,
        bottom: r.top + r.height
      });

      if (animate) {
        this.props.xScrollActions.animateRectIntoView(rect);
        this.props.yScrollActions.animateRectIntoView(rect);
      } else {
        this.props.xScrollActions.jumpRectIntoView(rect);
        this.props.yScrollActions.jumpRectIntoView(rect);
      }
    }
  }

  onStickyNodeScroll(_e) {
    this._stickyDomNode.scrollTop = 0;
  }

  onLiveScrollStart(horizontalScroller, verticalScroller) {
    if (horizontalScroller) {
      this._horizontalScrollerActive = true;
    }
    if (verticalScroller) {
      this._verticalScrollerActive = true;
    }
    this.applyStyles();
    this.applyTooltipStyle();
  }

  onLiveScrollUpdate(scrollLeft, scrollTop) {
    // Update both liveTop and liveLeft as needed, but we only need to applyStyles when
    // we are changing top.
    if (!isNil(scrollLeft) && scrollLeft !== this._liveLeft) {
      this._liveLeft = scrollLeft;
    }
    if (!isNil(scrollTop) && scrollTop !== this._liveTop) {
      this._liveTop = scrollTop;
      this.applyStyles();
    }
  }

  onLiveScrollStop(horizontalScroller, verticalScroller) {
    if (horizontalScroller) {
      this._horizontalScrollerActive = false;
    }
    if (verticalScroller) {
      this._verticalScrollerActive = false;
    }
    this.applyStyles();
    this.applyTooltipStyle();
  }

  applyStyles() {
    const styles = this.getStyles();
    this._stickyDomNode.style.left = styles.nodeStyle.left;
    this._stickyDomNode.style.top = styles.nodeStyle.top;
    this._stickyDomNode.style.height = styles.nodeStyle.height;
    this._stickyDomNode.style.overflow = styles.nodeStyle.overflow;
    this._stickyDomNode.style.position = styles.nodeStyle.position;
    this._stickyTransformNode.style.transform = styles.transformStyle.transform;
  }

  getStyles() {
    // Node styles
    let left = `${this._liveLeft}px`;
    let top = "0px";
    let height = null;
    let overflow = null;
    let position = "absolute";
    const maxWidth = `${this.props.containerRect.width}px`;

    // Transform style
    let transform = null;

    if (this._horizontalScrollerActive) {
      left = `${this.props.containerRect.left}px`;
      top = `${this.props.containerRect.top}px`;
      height = `${this.props.containerRect.height}px`;
      position = "fixed";
      overflow = "hidden";
      transform = `translateY(${-this._liveTop}px)`;
    }

    const nodeStyle = {
      left,
      top,
      height,
      overflow,
      position,
      maxWidth
    };
    const transformStyle = {
      transform
    };

    return {
      nodeStyle,
      transformStyle
    };
  }

  getTooltipStyle(columns) {
    if (columns) {
      let left = 0;
      columns.map(c => {
        if (c.id === this.props.reorderSortByColumn) {
          this._sortByColLeft = left;
          this._sortByColWidth = c.width;
        }
        left += c.width;
      });
    }

    let sortByColNodeTop = this.props.yScrollPosition.relativeScroll;
    let sortByColNodeLeft = 0;
    let sortByColTooltipNodeClass = null;

    const containerRectWidth = this.props.containerRect.width;
    const showLeftTooltip = this._liveLeft > this._sortByColLeft;
    const showRightTooltip =
      this._liveLeft + containerRectWidth - this._sortByColWidth - this._stickyWidth < this._sortByColLeft;

    if (showLeftTooltip) {
      sortByColNodeTop = sortByColNodeTop + "px";
      sortByColNodeLeft = this._stickyWidth + this._liveLeft + "px";
      sortByColTooltipNodeClass = "left";
    } else if (showRightTooltip) {
      sortByColNodeTop = sortByColNodeTop + "px";
      sortByColNodeLeft = this._liveLeft + containerRectWidth - this._sortByColWidth + "px";
      sortByColTooltipNodeClass = "right";
    } else {
      sortByColNodeTop = sortByColNodeTop + 10 + "px";
      sortByColNodeLeft = this._stickyWidth + this._sortByColLeft + "px";
      sortByColTooltipNodeClass = "bottom";
    }

    return { sortByColNodeTop, sortByColNodeLeft, sortByColTooltipNodeClass };
  }

  applyTooltipStyle() {
    const { sortByColNodeTop, sortByColNodeLeft, sortByColTooltipNodeClass } = this.getTooltipStyle();

    if (this._sortByColNode && this._sortByColTooltipNode) {
      this._sortByColNode.style.top = sortByColNodeTop;
      this._sortByColNode.style.left = sortByColNodeLeft;

      this._sortByColTooltipNode.classList.remove("left");
      this._sortByColTooltipNode.classList.remove("right");
      this._sortByColTooltipNode.classList.remove("bottom");
      this._sortByColTooltipNode.classList.add(sortByColTooltipNodeClass);
    }
  }

  showSortByTooltipIfRequired() {
    if (this.props.reorderSortByColumn) {
      if (this.props.sortByColumnId == this.props.reorderSortByColumn) {
        this.setState({ sortByTooltipOpen: false });
      } else {
        this.setState({ sortByTooltipOpen: true }, () => {
          this.resetSortByTooltipTimer();
          this.applyTooltipStyle();
        });
      }
    }
  }

  resetSortByTooltipTimer() {
    clearTimeout(sortByTooltipTimer);
    sortByTooltipTimer = setTimeout(() => {
      this.setState({ sortByTooltipOpen: false });
    }, 3000);
  }

  closeSortByTooltip() {
    clearTimeout(sortByTooltipTimer);
    this.setState({ sortByTooltipOpen: false });
  }

  renderRows(fromColumn, toColumn, isSticky) {
    const { data } = this.props;
    return this.state.visibleRows
      .map(row => {
        const obj = row.toObject();
        const isDragging = this.props.draggedRowIds && this.props.draggedRowIds.contains(obj.id);
        const top = row.top;
        const left = 0;
        const canDrag = row.id === tableRowTypes.EMPTY || !isSticky ? false : this.props.allowRowDrag;

        const focusedCell =
          this.props.focusedCell &&
          this.props.focusedCell.row === row.index &&
          this.props.focusedCell.column >= fromColumn &&
          this.props.focusedCell.column <= toColumn
            ? this.props.focusedCell
            : null;

        const cellSelection = utils.isRowPartOfSelection(this.props.cellSelection, row.index)
          ? this.props.cellSelection
          : null;

        // Only use the draggable component for the left column with the gutter
        const RowComponent = canDrag ? DraggableTableRowView : TableRowView;
        return (
          <RowComponent
            key={obj.id}
            {...obj}
            registerCellFocusCallback={this.props.registerCellFocusCallback}
            unregisterCellFocusCallback={this.props.unregisterCellFocusCallback}
            top={top}
            left={left}
            tableId={this.props.tableId}
            viewId={this.props.viewId}
            isSelected={this.props.selectedRowIds && this.props.selectedRowIds.contains(obj.id)}
            cellSelection={cellSelection}
            focusedCell={focusedCell}
            allColumns={data.columns}
            fromColumn={fromColumn}
            toColumn={toColumn}
            calendar={this.props.calendar}
            autoAdjustEndDate={this.props.autoAdjustEndDate}
            onToggleChildren={this.props.onToggleChildren}
            onGutterClick={this.props.onRowGutterClick}
            onCellClick={this.props.onCellClick}
            onCellPopoverToggle={this.props.onCellPopoverToggle}
            onCellCut={this.props.onCellCut}
            onCellCopy={this.props.onCellCopy}
            onCellPaste={this.props.onCellPaste}
            onCellDelete={this.props.onCellDelete}
            onCellDoubleClick={this.props.onCellDoubleClick}
            onValueChanged={this.props.onValueChanged}
            onMultiValueChanged={this.props.onMultiValueChanged}
            moveFocus={this.props.moveFocus}
            getCellId={this.props.getCellId}
            canDrag={canDrag}
            showGutter={isSticky}
            gutterIcon={this.props.rowGutterIcon}
            gutterIconColor={this.props.rowGutterIconColor}
            pulsateIconId={this.props.pulsateIconId}
            isDraggingRow={isDragging}
            getCellOptions={this.props.getCellOptions}
            showViolatedConstraints={this.props.showViolatedConstraints}
            onShowInspector={this.props.onShowInspector}
          />
        );
      })
      .toArray();
  }

  renderSelectionOutlines(fromColumn, toColumn, isSticky) {
    if (toColumn - fromColumn > 0) {
      const { data, cellSelection } = this.props;
      const lastColumnIndex = toColumn < 1 ? 0 : toColumn - 1;
      const groupOffset = data.columns.get(fromColumn).left;
      if (cellSelection.groups.size > 1 || data.rows.size === 0) {
        return null;
      }

      return cellSelection.groups
        .map((group, groupIndex) => {
          // If the selection falls outside of this column group, ignore it.
          if (group.topLeft.column > lastColumnIndex || group.bottomRight.column < fromColumn) {
            return null;
          }

          const leftIndex = Math.max(group.topLeft.column, fromColumn);
          const rightIndex = Math.min(group.bottomRight.column, lastColumnIndex);

          // Access the actual rows and columns
          const topRow = data.rows.get(group.topLeft.row);
          const bottomRow = data.rows.get(group.bottomRight.row);
          const leftColumn = data.columns.get(leftIndex);
          const rightColumn = data.columns.get(rightIndex);

          const xAdjust =
            (this.props.showRowGutter && isSticky
              ? scalePixelsToFontSize(OUTLINE_CELL_ROW_GUTTER_WIDTH, this.props.fontSize)
              : 0) - groupOffset;

          const top = topRow.top;
          const bottom = bottomRow.top + bottomRow.height;
          const left = leftColumn.left + xAdjust;
          const right = rightColumn.left + rightColumn.width + xAdjust;

          let className = "tableSelectionBorder";
          if (rightIndex === group.bottomRight.column) {
            className += " withRight";
          }
          if (leftIndex === group.topLeft.column) {
            className += " withLeft";
          }
          const style = {
            left: `${left}px`,
            width: `${right - left}px`,
            top: `${top}px`,
            height: `${bottom - top}px`
          };
          return <div key={groupIndex} className={className} style={style} />;
        })
        .filter(d => d)
        .toArray();
    }
    return null;
  }

  renderDropLines() {
    const {
      data,
      drawDropAction: origDropAction,
      drawDropRow: origDropRow,
      xScrollPosition,
      yScrollPosition,
      containerRect
    } = this.props;

    if (!origDropRow || !origDropAction || origDropRow.index >= data.rows.size) {
      return null;
    }

    // Adjust when dropping on the empty row
    const isDropOnEmpty = this._dropRow && this._dropRowIndex === data.rows.size - 1;
    // Do not adjust if it is DropTopLevel
    const drawDropAction =
      origDropAction !== tableDropAction.DropTopLevel && isDropOnEmpty ? tableDropAction.DropAboveRow : origDropAction;
    const drawDropRow = isDropOnEmpty ? data.rows.last() : origDropRow;

    const row = data.rows.get(drawDropRow.index);
    let left = 0;
    let width = xScrollPosition.canvas.range;
    let top = null;
    let height = null;
    let childDropIndicatorElement = null;
    const classes = [];

    if (drawDropAction === tableDropAction.DropTopLevel) {
      left = xScrollPosition.relativeScroll;
      top = yScrollPosition.relativeScroll;
      height = containerRect.height;
      width = Math.min(containerRect.width, xScrollPosition.canvas.range);
      classes.push("tableDropIndicator");
      classes.push("dropTopLevel");
    } else if (drawDropAction === tableDropAction.DropAboveRow) {
      top = drawDropRow.index === 0 ? row.top : row.top - 1;
      height = 2;
      classes.push("tableDropIndicator");
      classes.push(`indent${drawDropRow.indentationLevel > 6 ? "Deep" : drawDropRow.indentationLevel}`);
      classes.push("dropLine");
    } else if (drawDropAction === tableDropAction.DropBelowRow) {
      top = row.top + row.height - 1;
      height = 2;
      classes.push("tableDropIndicator");
      classes.push(`indent${drawDropRow.indentationLevel > 6 ? "Deep" : drawDropRow.indentationLevel}`);
      classes.push("dropLine");
    } else if (drawDropAction === tableDropAction.DropOnRow) {
      top = drawDropRow.index === 0 ? row.top : row.top - 1;
      height = drawDropRow.index === 0 ? row.height + 1 : row.height + 2;
      classes.push("tableDropIndicator");
      classes.push("dropOn");

      // Outline the parent's children if dropping within children while sorted by non-sequence
      if (this._dropAsChild) {
        const nextParent = data.rows.findIndex(
          (r, index) => r.indentationLevel <= row.indentationLevel && index > drawDropRow.index
        );
        const lastChildIndex = nextParent > -1 ? nextParent - 1 : data.rows.length - 1;
        const lastChildRow = data.rows.get(lastChildIndex);

        const childDropIndicatorLeft = xScrollPosition.relativeScroll;
        const childDropIndicatorBottom = lastChildRow.top + lastChildRow.height;
        const childDropIndicatorHeight = childDropIndicatorBottom - top;
        const childDropIndicatorWidth = Math.min(containerRect.width, xScrollPosition.canvas.range);
        childDropIndicatorElement = (
          <div
            key="tableChildDropIndicator"
            className="tableDropIndicator dropTopLevel"
            style={{
              left: `${childDropIndicatorLeft}px`,
              width: `${childDropIndicatorWidth}px`,
              top: `${top}px`,
              height: `${childDropIndicatorHeight}px`
            }}
          />
        );
      }
    } else {
      return null;
    }
    const style = {
      left: `${left}px`,
      width: `${width}px`,
      top: `${top}px`,
      height: `${height}px`
    };

    const dropLineElement = <div className={classes.join(" ")} style={style} key="tableDropIndicator" />;
    return childDropIndicatorElement ? [dropLineElement, childDropIndicatorElement] : dropLineElement;
  }

  renderStickyRows(stickyWidth, _scrollingWidth) {
    const { nodeStyle } = this.getStyles();
    nodeStyle.width = `${stickyWidth}px`;

    return (
      <div
        key="tableBodySticky"
        className="tableBodySticky"
        style={nodeStyle}
        ref={node => (this._stickyDomNode = node)}
        onScroll={this.onStickyNodeScroll}
      >
        <div className="tableBodyStickyTransform" ref={node => (this._stickyTransformNode = node)}>
          {this.renderRows(0, this.props.lockedColumnCount, true)}
          {this.props.lockedColumnCount > 0
            ? this.renderSelectionOutlines(0, this.props.lockedColumnCount, true)
            : null}
        </div>
      </div>
    );
  }

  renderScrollingRows(stickyWidth, scrollingWidth) {
    const style = {
      left: `${stickyWidth}px`,
      width: `${scrollingWidth}px`
    };

    return (
      <div key="tableBodyScrolling" className="tableBodyScrolling" style={style}>
        {this.renderRows(this.props.lockedColumnCount, this.props.data.columns.size, false)}
        {this.renderSelectionOutlines(this.props.lockedColumnCount, this.props.data.columns.size, false)}
      </div>
    );
  }

  renderTooltip(columns) {
    let message = "Sort by # to allow row reordering";
    const { sortByColNodeTop, sortByColNodeLeft, sortByColTooltipNodeClass } = this.getTooltipStyle(columns);

    const tooltip = (
      <Fragment key="tooltip">
        <div
          style={{
            left: sortByColNodeLeft,
            top: sortByColNodeTop,
            width: this._sortByColWidth
          }}
          className={`tableHeaderCellSortBy`}
          ref={node => (this._sortByColNode = node)}
        >
          <div
            className={`tableHeaderCellSortByTooltip popoverTooltipLabel ${sortByColTooltipNodeClass}`}
            ref={node => (this._sortByColTooltipNode = node)}
          >
            {message}
          </div>
        </div>
      </Fragment>
    );
    return tooltip;
  }

  render() {
    const canvasWidth = this.props.xScrollPosition.canvas.max - this.props.xScrollPosition.canvas.min;
    const canvasHeight = this.props.yScrollPosition.canvas.max - this.props.yScrollPosition.canvas.min;

    const bodyStyle = {
      width: `${canvasWidth}px`,
      height: `${canvasHeight - this.props.data.headerHeight}px`
    };

    const lastStickyColumn =
      this.props.lockedColumnCount > 0 ? this.props.data.columns.get(this.props.lockedColumnCount - 1) : null;

    const lastStickyRight = lastStickyColumn ? lastStickyColumn.left + lastStickyColumn.width : 0;

    const lastColumn = this.props.data.columns.last();
    const lastRight = lastColumn.left + lastColumn.width;

    const stickyWidth =
      lastStickyRight +
      (this.props.showRowGutter ? scalePixelsToFontSize(OUTLINE_CELL_ROW_GUTTER_WIDTH, this.props.fontSize) : 0);
    const scrollingWidth = lastRight - lastStickyRight;

    this._scrollingWidth = scrollingWidth;
    this._stickyWidth = stickyWidth;

    return (
      <div className="tableBodyView" style={bodyStyle} onFocus={this.props.onFocusIn} onBlur={this.props.onFocusOut}>
        {this.renderStickyRows(stickyWidth, scrollingWidth)}
        {this.renderScrollingRows(stickyWidth, scrollingWidth)}
        {this.renderDropLines()}
        {this.state.sortByTooltipOpen
          ? this.renderTooltip(this.props.data.columns.slice(this.props.lockedColumnCount))
          : null}
      </div>
    );
  }
}

TableBodyView.propTypes = {
  // Data from Table
  viewId: PropTypes.string.isRequired,
  tableId: PropTypes.string.isRequired,
  data: PropTypeShapes.tableData.isRequired,

  // For scroll to folded row
  getItemIdsToExpand: PropTypes.func,

  // Can the user sort the table?
  allowSort: PropTypes.bool,
  sortableColumnIds: ImmutablePropTypes.listOf(PropTypes.string),
  sortByColumnId: PropTypes.string,
  onSortByColumn: PropTypes.func,

  // How many columns on the left are locked in place?
  lockedColumnCount: PropTypes.number.isRequired,

  // Which cells are selected?
  selectedRowIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  cellSelection: PropTypes.instanceOf(immutableRecords.TableCellSelectionRecord).isRequired,
  focusedCell: PropTypes.instanceOf(immutableRecords.TableCellPositionRecord),

  // Do we need to draw any drop locators...?
  drawDropRow: PropTypeShapes.tableRow,
  drawDropAction: PropTypes.string,

  // Used for DatePicker
  calendar: PropTypeShapes.calendar,
  autoAdjustEndDate: PropTypes.bool,

  // Registering cells with TableView
  registerCellFocusCallback: PropTypes.func.isRequired,
  unregisterCellFocusCallback: PropTypes.func.isRequired,

  // Used for dependencies
  getCellOptions: PropTypes.func,

  // Called whenever a user clicks to toggle a parent.
  // Expect the view to go and reload the remaining data...?
  onToggleChildren: PropTypes.func.isRequired,
  // Called whenever a value is changed for a cell
  onValueChanged: PropTypes.func.isRequired,
  onMultiValueChanged: PropTypes.func.isRequired,

  // And internal table functions
  onFocusIn: PropTypes.func.isRequired,
  onFocusOut: PropTypes.func.isRequired,
  moveFocus: PropTypes.func.isRequired,
  getCellId: PropTypes.func.isRequired,
  onCellClick: PropTypes.func.isRequired,
  onCellDoubleClick: PropTypes.func.isRequired,
  onCellPopoverToggle: PropTypes.func.isRequired,
  onCellCut: PropTypes.func.isRequired,
  onCellCopy: PropTypes.func.isRequired,
  onCellPaste: PropTypes.func.isRequired,
  onCellDelete: PropTypes.func.isRequired,
  onRowGutterClick: PropTypes.func,

  // Can the user drag rows
  allowRowDrag: PropTypes.bool,
  // Can the user drag rows to create nested groups.
  // If Yes, indicators will be used to show where an item should drop.
  // If No, rows will move out of the way and leave a gap where they belong.
  allowRowDragNesting: PropTypes.bool,
  // What rows are currently being dragged...?
  draggedRowIds: ImmutablePropTypes.list,

  // Does this group of columns show the gutter...?
  showRowGutter: PropTypes.bool,
  rowGutterIcon: PropTypes.string,
  rowGutterIconColor: PropTypes.string,

  // Dragging rows
  onDragLeave: PropTypes.func,
  onDragEnter: PropTypes.func,
  onLiveRowDrag: PropTypes.func,
  onRowDrop: PropTypes.func,

  canItemDropOnRow: PropTypes.func.isRequired,
  canRowDrop: PropTypes.bool.isRequired,
  canTypeHaveSuperType: PropTypes.func.isRequired,
  requestCanDropItems: PropTypes.func.isRequired,
  adjustItemEndWhenChangingStart: PropTypes.bool,
  displayItems: ImmutablePropTypes.map,
  requestDragPreviewDataForItem: PropTypes.func.isRequired,
  showViolatedConstraints: PropTypes.bool,
  onShowInspector: PropTypes.func,
  disableReordering: PropTypes.bool,
  reorderSortByColumn: PropTypes.string,

  // From withScrollPositionContext
  xScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord).isRequired,
  xScrollActions: PropTypeShapes.scrollActions.isRequired,
  yScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord).isRequired,
  yScrollActions: PropTypeShapes.scrollActions.isRequired,

  // From asDropTarget
  registerDropTarget: PropTypes.func.isRequired,

  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired,

  fontSize: PropTypes.number.isRequired,
  pulsateIconId: PropTypes.string
};

const scrollableViewOptions = {
  scrollHorizontally: false,
  scrollVertically: true,
  showHorizontalScrollbar: false,
  showVerticalScrollbar: true
};

export default withContainerContext(
  withScrollableView(withScrollPositionContext(asDropTarget(TableBodyView, TABLE_VIEW)), scrollableViewOptions)
);
